import { auth, db } from '../config/firebase';
import { sendPasswordResetEmail, signInWithEmailAndPassword, signInWithPopup, signOut as fbSignOut, GoogleAuthProvider, FacebookAuthProvider } from "firebase/auth";
import { useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CssBaseline,
    Divider,
    Grid,
    InputAdornment,
    Snackbar,
    TextField,
    Typography,
    Paper,
} from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import { Link, useNavigate } from 'react-router-dom';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import GoogleIcon from '@mui/icons-material/Google';
import { getDoc, setDoc, doc } from "firebase/firestore";
import { USER_TYPES } from '../constants/userTypes';

// Style tokens for projectDrawer look & feel
const drawerBg = "background.paper";
const drawerBorder = "1px solid";
const drawerBorderColor = "divider";
const drawerBoxShadow = 1;
const drawerPadding = 4;
const drawerRadius = 3;

export const Login = () => {
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [errorOpen, setErrorOpen] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = useState('');
    const [passwordError, setPasswordError] = useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
    const [resetSuccess, setResetSuccess] = useState(false);
    const [resetError, setResetError] = useState(false);
    const [resetMessage, setResetMessage] = useState('');
    const emailRef = useRef();
    const passwordRef = useRef();
    const recaptchaRef = useRef(null);
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    const navigate = useNavigate();

    // Validate login
    const validateInputs = () => {
        const emailValue = emailRef.current?.value || '';
        const passwordValue = passwordRef.current?.value || '';
        let isValid = true;

        if (!emailValue || !/\S+@\S+\.\S+/.test(emailValue)) {
            setEmailError(true);
            setEmailErrorMessage('Please enter a valid email address.');
            isValid = false;
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }

        if (!passwordValue || passwordValue.length < 6) {
            setPasswordError(true);
            setPasswordErrorMessage('Password must be at least 6 characters long.');
            isValid = false;
        } else {
            setPasswordError(false);
            setPasswordErrorMessage('');
        }

        return isValid;
    };

    // Validate forgot password
    const validateInputsReset = (resetValue) => {
        let isValid = true;
        if (!resetValue || !/\S+@\S+\.\S+/.test(resetValue)) {
            setEmailError(true);
            setEmailErrorMessage('Please enter a valid email address.');
            isValid = false;
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }
        return isValid;
    };

    // Form handlers
    const signIn = async (event) => {
        event.preventDefault();
        if (!validateInputs()) return;

        const email = emailRef.current.value;
        const password = passwordRef.current.value;

        if (siteKey && recaptchaRef.current) {
            //await recaptchaRef.current.executeAsync();
            //recaptchaRef.current.reset();

            const token = await recaptchaRef.current.getValue();
            if (!token) {
                return;
            }
            recaptchaRef.current.reset();
        }
        // Auth
        signInWithEmailAndPassword(auth, email, password)
            .then(() => navigate("/myprojects"))
            .catch(() => {
                setErrorOpen(true);
                emailRef.current.value = "";
                passwordRef.current.value = "";
                emailRef.current.focus();
            });
    };

    const resetPassword = async (event) => {
        event.preventDefault();
        const resetValue = event.target.elements.emailReset?.value;
        if (!validateInputsReset(resetValue)) return;

        try {
            await sendPasswordResetEmail(auth, resetValue);
            setResetSuccess(true);
            setResetMessage('Password reset email sent! Check your inbox.');
            setResetError(false);
            event.target.reset();
        } catch (error) {
            setResetError(true);
            setResetSuccess(false);
            switch (error.code) {
                case 'auth/user-not-found':
                    setResetMessage('No account found with this email address.');
                    break;
                case 'auth/invalid-email':
                    setResetMessage('Please enter a valid email address.');
                    break;
                case 'auth/too-many-requests':
                    setResetMessage('Too many requests. Please try again later.');
                    break;
                default:
                    setResetMessage('Failed to send reset email. Please try again.');
            }
        }
    };

    // Social
    const signInProvider = (provider) => {
        signInWithPopup(auth, provider)
            .then(async (result) => {
                const user = result.user;
                const updatedData = {
                    name: user.displayName || "",
                    bio: "This is my bio",
                    picurl: user.photoURL || "",
                    email: user.email || "",
                    uid: user.uid,
                    type: USER_TYPES.FREE
                };
                const userDocRef = doc(db, "user", user.uid);
                const userSnap = await getDoc(userDocRef);
                if (!userSnap.exists()) await setDoc(userDocRef, updatedData);
                navigate("/myprojects");
            })
            .catch((error) => {
                // Could show toast error here as well
                // eslint-disable-next-line
                console.log(error.message);
            });
    };

    const signInGoogle = () => {
        const provider = new GoogleAuthProvider();
        signInProvider(provider);
    };
    // const signInFb = () => {
    //     const provider = new FacebookAuthProvider();
    //     signInProvider(provider);
    // };

    // Snackbar
    const handleClose = () => setErrorOpen(false);

    return (
        <Box
            sx={{
                width: { xs: "100%", sm: 420 },
                mx: "auto",
                mt: { xs: 6, sm: 8 },
                border: drawerBorder,
                borderColor: drawerBorderColor,
                borderRadius: drawerRadius,
                boxShadow: drawerBoxShadow,
                bgcolor: drawerBg,
                px: drawerPadding,
                pt: 6,
                pb: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }}
        >
            <CssBaseline />

            <img 
                alt="NodeCanvas logo"
                src="/logo192.png"
                width={60}
                height={60}
                style={{marginBottom: 16, opacity: 0.9}}
            />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {showForgotPassword ? "Password Recovery" : "Welcome to NodeCanvas"}
            </Typography>

            <Typography 
                align="center" 
                color="text.secondary" 
                fontSize={15} 
                sx={{ mt: 1, mb: showForgotPassword ? 3 : 2 }}>
                {showForgotPassword ? 
                    "Enter your email and we'll email you a password recovery code." :
                    <>Don't have an account yet?{" "}
                        <Link to="/signup"><b>Sign up for free</b></Link>
                    </>
                }
            </Typography>

            {!showForgotPassword ? (
                <Box
                    component="form"
                    noValidate
                    autoComplete="off"
                    onSubmit={signIn}
                    sx={{ width: "100%" }}
                >
                    <TextField
                        id="email"
                        label="Email Address"
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        error={emailError}
                        helperText={emailErrorMessage}
                        autoComplete="email"
                        type="email"
                        autoFocus
                        inputRef={emailRef}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <EmailOutlinedIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            mb: 1.5,
                        }}
                    />
                    <TextField
                        id="password"
                        label="Password"
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        error={passwordError}
                        helperText={passwordErrorMessage}
                        type="password"
                        autoComplete="current-password"
                        inputRef={passwordRef}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockOpenOutlinedIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            mb: 2,
                        }}
                    />
                    
                    {siteKey && (
                        <Box display="flex" justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                size="normal"
                                sitekey={siteKey}
                            />
                        </Box>
                    )}
                    <Button
                        type="submit"
                        size="large"
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ mt: 1, mb: 1.5, fontWeight: 600, textTransform: 'none' }}
                    >
                        Log In
                    </Button>
                </Box>
            ) : (
                <Box
                    component="form"
                    noValidate
                    autoComplete="off"
                    onSubmit={resetPassword}
                    sx={{ width: "100%" }}
                >
                    <TextField
                        id="emailReset"
                        name="emailReset"
                        label="Email Address"
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        error={emailError}
                        helperText={emailErrorMessage}
                        autoComplete="email"
                        type="email"
                        autoFocus
                        // reuse reference purposely omitted for password recovery, as different ref
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <EmailOutlinedIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            mb: 2,
                        }}
                    />
                    <Button
                        type="submit"
                        size="large"
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ mt: 1, mb: 1.5, fontWeight: 600, textTransform: 'none' }}
                    >
                        Reset Password
                    </Button>
                </Box>
            )}

            {!showForgotPassword ? (
                <>
                    <Divider sx={{ my: 2, width: "100%", fontSize: 12 }}>or</Divider>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={signInGoogle}
                        startIcon={<GoogleIcon />}
                        sx={{ textTransform: 'none', fontWeight: 600, mb: 1 }}
                    >
                        Log in with Google
                    </Button>
                    {/* Uncomment below if Facebook Login is enabled */}
                    {/* <Button
                        fullWidth
                        variant="outlined"
                        onClick={signInFb}
                        startIcon={<FacebookIcon />}
                        sx={{ textTransform: 'none', fontWeight: 600, mb: 1 }}
                    >
                        Log in with Facebook
                    </Button> */}
                    <Button
                        onClick={() => setShowForgotPassword(true)}
                        fullWidth
                        size="small"
                        sx={{
                            textTransform: 'none',
                            mt: 0.5,
                            color: "text.secondary"
                        }}
                    >
                        Forgot password?
                    </Button>
                </>
            ) : (
                <Button
                    onClick={() => setShowForgotPassword(false)}
                    fullWidth
                    size="small"
                    color="inherit"
                    sx={{
                        textTransform: 'none',
                        mt: 0.5
                    }}
                >
                    ⬅ Back to Login
                </Button>
            )}

            <Snackbar open={errorOpen} autoHideDuration={3000} onClose={handleClose}>
                <Alert
                    onClose={handleClose}
                    severity="error"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    Wrong user name or password!
                </Alert>
            </Snackbar>
            <Snackbar open={resetSuccess} autoHideDuration={5000} onClose={() => setResetSuccess(false)}>
                <Alert
                    onClose={() => setResetSuccess(false)}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {resetMessage}
                </Alert>
            </Snackbar>
            <Snackbar open={resetError} autoHideDuration={5000} onClose={() => setResetError(false)}>
                <Alert
                    onClose={() => setResetError(false)}
                    severity="error"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {resetMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};